import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router';
import {
  LifeBuoy,
  ArrowLeft,
  CheckCircle2,
  Send,
  Lock,
  UserCheck,
} from 'lucide-react';
import {
  Card,
  Button,
  StatusBadge,
  type StatusTone,
  NotificationCard,
  UniversalEmptyState,
  UniversalLoadingState,
  UniversalPage,
  Modal,
  ModalBody,
  ModalFooter,
  Input,
  FormField,
  useToast,
} from '@sighthost/ui';
import {
  adminTicketService,
  type AdminTicketRowEnriched,
  type TicketMessage,
  type TicketStatus,
  type TicketPriority,
  STATUS_LABELS,
} from '@/domains/support/services/adminTicketService';
import {
  ticketAttachmentService,
  type TicketAttachment,
} from '@/domains/support/services/ticketAttachmentService';
import {
  AttachmentChips,
  AttachmentPicker,
} from '@/domains/support/components/TicketAttachments';
import { DomainExecutePanel } from './DomainExecutePanel';

const STATUS_TONE: Record<TicketStatus, StatusTone> = {
  open: 'pending',
  pending_customer: 'info',
  closed: 'paid',
};

const PRIORITY_TONE: Record<TicketPriority, StatusTone> = {
  low: 'draft',
  normal: 'info',
  high: 'pending',
  urgent: 'overdue',
};

export default function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [ticket, setTicket] = useState<AdminTicketRowEnriched | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [staff, setStaff] = useState<{ id: string; email: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const [replyBody, setReplyBody] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [resolutionNote, setResolutionNote] = useState('');
  const [attachments, setAttachments] = useState<TicketAttachment[]>([]);
  const [file, setFile] = useState<File | null>(null);

  function refresh() {
    if (!id) return;
    setLoading(true);
    setError(null);
    Promise.all([
      adminTicketService.getOne(id),
      adminTicketService.listMessages(id),
      ticketAttachmentService.list(id),
    ])
      .then(([t, msgs, atts]) => {
        setTicket(t);
        setMessages(msgs);
        setAttachments(atts);
        setLoading(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });
  }

  useEffect(() => {
    refresh();
    adminTicketService.listStaff().then(setStaff).catch(() => {});
  }, [id]);

  async function submitReply() {
    if (!ticket || !replyBody.trim()) return;
    setSubmitting(true);
    try {
      const res = await adminTicketService.reply({
        ticket_id: ticket.id,
        body: replyBody,
        is_internal_note: isInternalNote,
      });
      if (file) {
        try {
          // Attaching to an internal note keeps the file staff-only (the RLS
          // + EF both exclude internal-note attachments from customers).
          const messageId = (res as { message_id?: string }).message_id;
          await ticketAttachmentService.attach(ticket.id, file, messageId);
        } catch (attachErr) {
          toast({
            tone: 'warning',
            title: 'Reply saved, but the attachment failed',
            body: attachErr instanceof Error ? attachErr.message : String(attachErr),
          });
        }
      }
      toast({
        tone: 'success',
        title: isInternalNote ? 'Internal note added' : 'Reply sent',
        body: isInternalNote
          ? 'Visible to staff only.'
          : 'Customer has been emailed.',
      });
      setReplyBody('');
      setIsInternalNote(false);
      setFile(null);
      refresh();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Could not post reply',
        body: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAssign(staffId: string | null) {
    if (!ticket || assigning) return;
    setAssigning(true);
    try {
      await adminTicketService.assign({ ticket_id: ticket.id, assigned_staff_id: staffId });
      toast({
        tone: 'success',
        title: staffId ? 'Ticket assigned' : 'Ticket unassigned',
        body: '',
      });
      refresh();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Could not assign',
        body: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setAssigning(false);
    }
  }

  async function submitClose() {
    if (!ticket) return;
    setSubmitting(true);
    try {
      await adminTicketService.close({
        ticket_id: ticket.id,
        resolution_note: resolutionNote.trim() || undefined,
      });
      toast({
        tone: 'success',
        title: 'Ticket closed',
        body: 'The customer can re-open by replying.',
      });
      setCloseModalOpen(false);
      setResolutionNote('');
      refresh();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Could not close',
        body: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <UniversalLoadingState variant="rows" count={6} />;
  if (error) {
    return (
      <NotificationCard tone="danger" title="Could not load ticket" body={error.message} />
    );
  }
  if (!ticket) {
    return (
      <UniversalEmptyState
        icon={LifeBuoy}
        title="Ticket not found"
        description="It may have been deleted, or you don't have access."
      />
    );
  }

  return (
    <UniversalPage
      title={
        <span className="flex items-center gap-2">
          <LifeBuoy className="h-5 w-5" />
          {ticket.number} — {ticket.subject}
        </span>
      }
      description={`Filed by ${ticket.customer_name} · ${ticket.department_name ?? '—'}`}
      actions={
        <Button variant="outline" size="sm" onClick={() => navigate('/admin/support')}>
          <ArrowLeft className="h-4 w-4" />
          Back to inbox
        </Button>
      }
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]">
        {/* Conversation */}
        <div className="space-y-4">
          <Card interactive={false}>
            <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-6 py-3">
              <div className="flex items-center gap-2">
                <StatusBadge tone={STATUS_TONE[ticket.status]} dot>
                  {STATUS_LABELS[ticket.status]}
                </StatusBadge>
                <StatusBadge tone={PRIORITY_TONE[ticket.priority]}>
                  {ticket.priority}
                </StatusBadge>
              </div>
              <div className="flex flex-wrap gap-2">
                {ticket.status !== 'closed' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCloseModalOpen(true)}
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Close ticket
                  </Button>
                )}
              </div>
            </header>

            {messages.length === 0 ? (
              <p className="px-6 py-6 text-sm text-muted-foreground">No messages.</p>
            ) : (
              <div className="divide-y divide-border">
                {messages.map((m) => (
                  <article
                    key={m.id}
                    className={
                      'px-6 py-4 ' +
                      (m.is_internal_note
                        ? 'bg-warning/10 border-l-4 border-warning'
                        : m.author_kind === 'staff'
                        ? 'bg-muted/30'
                        : '')
                    }
                  >
                    <header className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="font-medium uppercase tracking-wider text-foreground">
                        {m.author_kind}
                      </span>
                      {m.is_internal_note && (
                        <span className="inline-flex items-center gap-1 rounded bg-warning/10 px-1.5 py-0.5 text-warning-dark">
                          <Lock className="h-3 w-3" />
                          Internal note
                        </span>
                      )}
                      <span className="ml-auto tabular-nums">
                        {new Date(m.created_at).toLocaleString('en-ZA')}
                      </span>
                    </header>
                    <div className="whitespace-pre-wrap text-sm text-foreground">
                      {m.body}
                    </div>
                    <AttachmentChips
                      attachments={attachments.filter((a) => a.messageId === m.id)}
                    />
                  </article>
                ))}
              </div>
            )}
            {attachments.some((a) => a.messageId === null) && (
              <div className="border-t border-border px-6 py-3">
                <p className="text-xs font-medium text-muted-foreground">Ticket attachments</p>
                <AttachmentChips attachments={attachments.filter((a) => a.messageId === null)} />
              </div>
            )}
          </Card>

          {/* Reply box */}
          {ticket.status !== 'closed' && (
            <Card interactive={false}>
              <header className="border-b border-border px-6 py-3">
                <h3 className="text-sm font-semibold">
                  {isInternalNote ? 'Add internal note' : 'Reply to customer'}
                </h3>
              </header>
              <div className="space-y-3 px-6 py-4">
                <textarea
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  rows={6}
                  disabled={submitting}
                  placeholder={
                    isInternalNote
                      ? 'Internal-only context. Not visible to the customer.'
                      : 'Message the customer. They will receive an email and can reply via the portal.'
                  }
                  className="w-full rounded border border-input bg-card px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  maxLength={10000}
                />
                <AttachmentPicker file={file} onFileChange={setFile} disabled={submitting} />
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label className="flex items-center gap-2 text-sm text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={isInternalNote}
                      onChange={(e) => setIsInternalNote(e.target.checked)}
                      disabled={submitting}
                    />
                    Internal note (staff-only)
                  </label>
                  <Button
                    onClick={submitReply}
                    disabled={submitting || !replyBody.trim()}
                    loading={submitting}
                  >
                    <Send className="h-4 w-4" />
                    {isInternalNote ? 'Save note' : 'Send reply'}
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          {/* DT-1 (2026-05-11) — Execute panel for domains-department tickets */}
          {/* DT-1 queue renamed 'domains' -> 'domain-intent' (2026-07-26).
              Accept both so the Execute panel keeps rendering whichever side of
              the rename the database is on, and for any pre-rename tickets. */}
          {(ticket.department_name === 'domain-intent' ||
            ticket.department_name === 'domains') && (
            <DomainExecutePanel ticketId={ticket.id} onActionComplete={refresh} />
          )}

          <Card interactive={false}>
            <header className="border-b border-border px-4 py-2">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Customer
              </h3>
            </header>
            <div className="space-y-1 px-4 py-3 text-sm">
              <p className="font-medium text-foreground">{ticket.customer_name}</p>
              {ticket.customer_email && (
                <p className="text-xs text-muted-foreground">{ticket.customer_email}</p>
              )}
              <Link
                to={`/admin/customers/${ticket.customer_id}`}
                className="text-xs text-primary-ink hover:underline"
              >
                Open customer record →
              </Link>
            </div>
          </Card>

          <Card interactive={false}>
            <header className="border-b border-border px-4 py-2">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Assignment
              </h3>
            </header>
            <div className="space-y-2 px-4 py-3 text-sm">
              <select
                value={ticket.assigned_staff_id ?? ''}
                onChange={(e) => handleAssign(e.target.value || null)}
                disabled={assigning}
                className="h-9 w-full rounded border border-input bg-card px-2 text-sm"
              >
                <option value="">Unassigned</option>
                {staff.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.email})
                  </option>
                ))}
              </select>
              {ticket.assigned_staff_id && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleAssign(null)}
                  loading={assigning}
                  disabled={assigning}
                  className="w-full"
                >
                  <UserCheck className="h-4 w-4" />
                  Unassign
                </Button>
              )}
            </div>
          </Card>

          {(ticket.related_cpanel_username ||
            ticket.related_domain_name ||
            ticket.related_invoice_id) && (
            <Card interactive={false}>
              <header className="border-b border-border px-4 py-2">
                <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Related
                </h3>
              </header>
              <div className="space-y-1 px-4 py-3 text-sm">
                {ticket.related_cpanel_username && (
                  <p>
                    <span className="text-muted-foreground">cPanel: </span>
                    <code className="text-xs">
                      {ticket.related_cpanel_username}
                    </code>
                  </p>
                )}
                {ticket.related_domain_name && (
                  <p>
                    <span className="text-muted-foreground">Domain: </span>
                    <code className="text-xs">
                      {ticket.related_domain_name}
                    </code>
                  </p>
                )}
                {ticket.related_invoice_id && (
                  <p>
                    <Link
                      to={`/admin/customers/${ticket.customer_id}/billing`}
                      className="text-primary-ink hover:underline"
                    >
                      View related invoice →
                    </Link>
                  </p>
                )}
              </div>
            </Card>
          )}

          <Card interactive={false}>
            <header className="border-b border-border px-4 py-2">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Timeline
              </h3>
            </header>
            <dl className="space-y-1 px-4 py-3 text-xs">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Opened</dt>
                <dd className="tabular-nums">
                  {new Date(ticket.created_at).toLocaleString('en-ZA')}
                </dd>
              </div>
              {ticket.first_response_at && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">First response</dt>
                  <dd className="tabular-nums">
                    {new Date(ticket.first_response_at).toLocaleString('en-ZA')}
                  </dd>
                </div>
              )}
              {ticket.last_replied_at && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Last reply</dt>
                  <dd className="tabular-nums">
                    {new Date(ticket.last_replied_at).toLocaleString('en-ZA')}
                  </dd>
                </div>
              )}
              {ticket.closed_at && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Closed</dt>
                  <dd className="tabular-nums">
                    {new Date(ticket.closed_at).toLocaleString('en-ZA')}
                  </dd>
                </div>
              )}
              {ticket.department_sla_hours && (
                <div className="flex justify-between border-t border-border pt-1">
                  <dt className="text-muted-foreground">SLA</dt>
                  <dd>{ticket.department_sla_hours}h</dd>
                </div>
              )}
            </dl>
          </Card>
        </aside>
      </div>

      {/* Close modal */}
      {closeModalOpen && (
        <Modal
          open
          onOpenChange={(o) => !o && setCloseModalOpen(false)}
          title="Close ticket"
          description={`${ticket.number} — ${ticket.subject}`}
        >
          <ModalBody>
            <FormField
              label="Resolution note (optional)"
              htmlFor="resolution_note"
              helper="Internal record of how this was resolved. Not sent to the customer."
            >
              <Input
                id="resolution_note"
                value={resolutionNote}
                onChange={(e) => setResolutionNote(e.target.value)}
                placeholder="e.g. confirmed payment received, account unsuspended"
                disabled={submitting}
                maxLength={500}
              />
            </FormField>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              onClick={() => setCloseModalOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button onClick={submitClose} loading={submitting} disabled={submitting}>
              Close ticket
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </UniversalPage>
  );
}
